/**
 * Product Images Field Component
 * 
 * Enterprise-level product image gallery management for DynamicFormBuilder
 * Supports multiple images, drag & drop reordering, primary selection, and thumbnails
 * 
 * @version 1.0.0
 * @date December 2025
 */

'use client';

import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Upload, X, Star, StarOff, Trash2, ImageIcon, GripVertical,
  Loader2, AlertCircle, Eye, ZoomIn, ChevronLeft, ChevronRight,
  Camera, Image as ImageLucide, RotateCw, Check
} from 'lucide-react';
import { uploadFile } from '@/lib/fileUpload';
import { authService } from '@/lib/authService';
import { useTheme } from '@/contexts/ThemeContext';
import { compressImageFile } from '@/lib/compressImage';
import BrandedLoadingSpinner from '@/components/shared/BrandedLoadingSpinner';
import { notify } from '@/lib/notifications';

// ============================================================================
// TYPES
// ============================================================================

export interface ProductImage {
  id?: number;
  work_item_id?: string;
  file_name: string;
  file_path: string;
  file_size?: number;
  file_type?: string;
  mime_type?: string;
  is_primary: boolean;
  sort_order: number;
  alt_text?: string;
  caption?: string;
  width?: number;
  height?: number;
  thumbnail_url?: string;
  medium_url?: string;
  large_url?: string;
  color_dominant?: string;
  tags?: string[];
  metadata?: Record<string, unknown>;
  // Temp ID for new uploads before save
  temp_id?: string;
  // Upload status
  uploading?: boolean;
  progress?: number;
  error?: string;
}

interface ProductImagesFieldProps {
  /** Work item ID (required for existing items) */
  workItemId?: string;
  /** Current images value */
  value?: ProductImage[];
  /** Change handler */
  onChange: (images: ProductImage[]) => void;
  /** Maximum number of images */
  maxImages?: number;
  /** Whether field is disabled */
  disabled?: boolean;
  /** Whether to allow primary selection */
  showPrimarySelector?: boolean;
  /** Whether to allow captions */
  showCaptions?: boolean;
  /** Whether to show alt text input */
  showAltText?: boolean;
  /** Accepted file types */
  acceptedTypes?: string;
  /** Maximum file size in MB */
  maxSizeMB?: number;
  /** Field label */
  label?: string;
  /** Help text */
  helpText?: string;
  /** Is dark mode */
  isDark?: boolean;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const getAuthToken = (): string | null => {
  return authService.getTokens()?.access_token || null;
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const generateTempId = (): string => `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// ============================================================================
// IMAGE LIGHTBOX COMPONENT
// ============================================================================

interface LightboxProps {
  images: ProductImage[];
  currentIndex: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
}

function ImageLightbox({ images, currentIndex, onClose, onNavigate }: LightboxProps) {
  const currentImage = images[currentIndex];
  
  const handlePrev = useCallback(() => {
    if (currentIndex > 0) onNavigate(currentIndex - 1);
  }, [currentIndex, onNavigate]);
  
  const handleNext = useCallback(() => {
    if (currentIndex < images.length - 1) onNavigate(currentIndex + 1);
  }, [currentIndex, images.length, onNavigate]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, images.length, handleNext, handlePrev, onClose]);

  if (!currentImage) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center">
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
      >
        <X className="w-6 h-6" />
      </button>

      {/* Image counter */}
      <div className="absolute top-4 left-4 text-white text-sm bg-black/50 px-3 py-1 rounded-full">
        {currentIndex + 1} / {images.length}
      </div>

      {/* Navigation buttons */}
      {currentIndex > 0 && (
        <button
          onClick={handlePrev}
          className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
        >
          <ChevronLeft className="w-8 h-8" />
        </button>
      )}
      
      {currentIndex < images.length - 1 && (
        <button
          onClick={handleNext}
          className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
        >
          <ChevronRight className="w-8 h-8" />
        </button>
      )}

      {/* Main image */}
      <div className="max-w-[90vw] max-h-[85vh] relative">
        <img
          src={currentImage.large_url || currentImage.file_path}
          alt={currentImage.alt_text || currentImage.file_name}
          className="max-w-full max-h-[85vh] object-contain rounded-lg"
        />
        
        {/* Caption */}
        {currentImage.caption && (
          <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white p-4 rounded-b-lg">
            <p className="text-center">{currentImage.caption}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// SINGLE IMAGE CARD COMPONENT
// ============================================================================

interface ImageCardProps {
  image: ProductImage;
  index: number;
  onRemove: () => void;
  onSetPrimary: () => void;
  onView: () => void;
  onUpdateCaption?: (caption: string) => void;
  onUpdateAltText?: (altText: string) => void;
  showPrimarySelector: boolean;
  showCaptions: boolean;
  showAltText: boolean;
  disabled: boolean;
  isDark: boolean;
  isDragging?: boolean;
  dragHandleProps?: React.HTMLAttributes<HTMLElement>;
}

function ImageCard({
  image,
  index,
  onRemove,
  onSetPrimary,
  onView,
  onUpdateCaption,
  onUpdateAltText,
  showPrimarySelector,
  showCaptions,
  showAltText,
  disabled,
  isDark,
  isDragging,
  dragHandleProps
}: ImageCardProps) {
  const [editingCaption, setEditingCaption] = useState(false);
  const [captionValue, setCaptionValue] = useState(image.caption || '');

  const handleCaptionSave = () => {
    onUpdateCaption?.(captionValue);
    setEditingCaption(false);
  };

  return (
    <div
      className={`relative group rounded-lg overflow-hidden border-2 transition-all ${
        isDragging ? 'opacity-50 scale-95' : ''
      } ${
        image.is_primary
          ? isDark
            ? 'border-yellow-500 bg-yellow-900/20'
            : 'border-yellow-400 bg-yellow-50'
          : isDark
            ? 'border-gray-600 bg-gray-800'
            : 'border-gray-200 bg-white'
      }`}
    >
      {/* Primary badge */}
      {image.is_primary && (
        <div className={`absolute top-2 left-2 z-10 px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${
          isDark ? 'bg-yellow-500 text-black' : 'bg-yellow-400 text-yellow-900'
        }`}>
          <Star className="w-3 h-3" fill="currentColor" />
          Primary
        </div>
      )}

      {/* Upload progress overlay */}
      {image.uploading && (
        <div className="absolute inset-0 z-20 bg-black/60 flex items-center justify-center">
          <div className="text-center text-white">
            <BrandedLoadingSpinner mode="compact" size="sm" showSurface={false} showProgressBar={false} />
            <p className="text-sm">{image.progress || 0}%</p>
          </div>
        </div>
      )}

      {/* Error overlay */}
      {image.error && (
        <div className="absolute inset-0 z-20 bg-red-500/80 flex items-center justify-center p-2">
          <div className="text-center text-white">
            <AlertCircle className="w-6 h-6 mx-auto mb-1" />
            <p className="text-xs">{image.error}</p>
          </div>
        </div>
      )}

      {/* Image thumbnail */}
      <div className="aspect-square relative">
        <img
          src={image.thumbnail_url || image.file_path}
          alt={image.alt_text || image.file_name}
          className="w-full h-full object-cover"
          loading="lazy"
        />

        {/* Hover overlay with actions */}
        <div className={`absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100`}>
          {/* View button */}
          <button
            type="button"
            onClick={onView}
            className="p-2 rounded-full bg-white/90 text-gray-800 hover:bg-white transition-colors"
            title="View full size"
          >
            <ZoomIn className="w-4 h-4" />
          </button>

          {/* Set as primary */}
          {showPrimarySelector && !image.is_primary && !disabled && (
            <button
              type="button"
              onClick={onSetPrimary}
              className="p-2 rounded-full bg-yellow-400 text-yellow-900 hover:bg-yellow-300 transition-colors"
              title="Set as primary"
            >
              <Star className="w-4 h-4" />
            </button>
          )}

          {/* Remove button */}
          {!disabled && (
            <button
              type="button"
              onClick={onRemove}
              className="p-2 rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors"
              title="Remove image"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Drag handle */}
        {!disabled && (
          <div
            {...dragHandleProps}
            className="absolute top-2 right-2 p-1 rounded bg-black/50 text-white cursor-grab opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <GripVertical className="w-4 h-4" />
          </div>
        )}
      </div>

      {/* Image info and caption */}
      <div className={`p-2 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        <p className={`text-xs truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          {image.file_name}
        </p>
        {image.file_size && (
          <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            {formatFileSize(image.file_size)}
          </p>
        )}

        {/* Caption input */}
        {showCaptions && (
          <div className="mt-2">
            {editingCaption ? (
              <div className="flex gap-1">
                <input
                  type="text"
                  value={captionValue}
                  onChange={(e) => setCaptionValue(e.target.value)}
                  placeholder="Add caption..."
                  className={`flex-1 text-xs px-2 py-1 rounded border ${
                    isDark
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCaptionSave();
                    if (e.key === 'Escape') setEditingCaption(false);
                  }}
                  autoFocus
                />
                <button
                  type="button"
                  onClick={handleCaptionSave}
                  className="p-1 rounded bg-blue-500 text-white hover:bg-blue-600"
                >
                  <Check className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => !disabled && setEditingCaption(true)}
                className={`text-xs w-full text-left px-2 py-1 rounded truncate ${
                  isDark
                    ? 'text-gray-400 hover:bg-gray-700'
                    : 'text-gray-500 hover:bg-gray-100'
                } ${disabled ? 'cursor-default' : 'cursor-pointer'}`}
              >
                {image.caption || (disabled ? 'No caption' : 'Add caption...')}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function ProductImagesField({
  workItemId,
  value = [],
  onChange,
  maxImages = 10,
  disabled = false,
  showPrimarySelector = true,
  showCaptions = true,
  showAltText = false,
  acceptedTypes = 'image/jpeg,image/png,image/webp,image/gif',
  maxSizeMB = 10,
  label = 'Product Images',
  helpText,
  isDark: propIsDark
}: ProductImagesFieldProps) {
  const { currentTheme } = useTheme();
  const isDark = propIsDark ?? currentTheme.mode === 'dark';
  
  const [images, setImages] = useState<ProductImage[]>(value);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const hasLoadedRef = useRef(false);

  // Track object URLs created for temp previews so they can be revoked when
  // replaced by the real public URL, when the image is removed, or on unmount.
  // Leaking blob: URLs keeps the File/Blob alive in memory until document unload.
  const blobUrlsRef = useRef<Set<string>>(new Set());

  const trackBlobUrl = useCallback((url: string) => {
    if (url.startsWith('blob:')) blobUrlsRef.current.add(url);
  }, []);

  const revokeBlobUrl = useCallback((url?: string) => {
    if (url && url.startsWith('blob:') && blobUrlsRef.current.has(url)) {
      URL.revokeObjectURL(url);
      blobUrlsRef.current.delete(url);
    }
  }, []);

  // Revoke any still-tracked object URLs on unmount.
  useEffect(() => {
    const tracked = blobUrlsRef.current;
    return () => {
      tracked.forEach((url) => URL.revokeObjectURL(url));
      tracked.clear();
    };
  }, []);

  // Sync with value prop
  useEffect(() => {
    if (JSON.stringify(value) !== JSON.stringify(images)) {
      setImages(value);
    }
  }, [value, images]);

  const loadExistingImages = useCallback(async () => {
    if (!workItemId) return;
    
    setLoadingExisting(true);
    try {
      const token = getAuthToken();
      const response = await fetch(`/api/v1/work-items/${workItemId}/product-images`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          const loadedImages = (data.data as ProductImage[]).map((img) => ({
            ...img,
            is_primary: img.is_primary || false,
            sort_order: img.sort_order || 0
          }));
          setImages(loadedImages);
          onChange(loadedImages);
        }
      }
    } catch (err) {
      console.error('Error loading product images:', err);
    } finally {
      setLoadingExisting(false);
    }
  }, [workItemId, onChange]);

  // Load existing images when workItemId is provided
  useEffect(() => {
    if (workItemId && !hasLoadedRef.current) {
      loadExistingImages();
      hasLoadedRef.current = true;
    }
  }, [workItemId, loadExistingImages]);

  const updateTempImage = useCallback((tempId: string, updates: Partial<ProductImage>) => {
    setImages(prev => {
      const updated = prev.map(img => {
        if (img.temp_id !== tempId) return img;
        // Revoke the temp blob: preview once it's replaced by the real URL.
        if (updates.file_path && img.file_path && img.file_path !== updates.file_path) {
          revokeBlobUrl(img.file_path);
        }
        return { ...img, ...updates };
      });
      onChange(updated);
      return updated;
    });
  }, [onChange, revokeBlobUrl]);

  const saveImageToDatabase = useCallback(async (image: ProductImage): Promise<ProductImage | null> => {
    if (!workItemId) return null;

    try {
      const token = getAuthToken();
      const response = await fetch(`/api/v1/work-items/${workItemId}/product-images`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          file_name: image.file_name,
          file_path: image.file_path,
          file_size: image.file_size,
          file_type: image.file_type,
          mime_type: image.mime_type,
          is_primary: image.is_primary,
          sort_order: image.sort_order,
          alt_text: image.alt_text,
          caption: image.caption
        })
      });

      if (response.ok) {
        const data = await response.json();
        return data.data;
      }
    } catch (err) {
      console.error('Error saving image to database:', err);
    }
    return null;
  }, [workItemId]);

  const handleFilesSelected = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0 || disabled) return;

    // Check limit
    const remainingSlots = maxImages - images.length;
    if (remainingSlots <= 0) {
      notify.info(`Maximum ${maxImages} images allowed`);
      return;
    }

    const filesToUpload = Array.from(files).slice(0, remainingSlots);
    
    // Create temporary image entries
    const tempImages: ProductImage[] = filesToUpload.map((file, idx) => {
      const objectUrl = URL.createObjectURL(file);
      trackBlobUrl(objectUrl);
      return {
        temp_id: generateTempId(),
        file_name: file.name,
        file_path: objectUrl,
        file_size: file.size,
        file_type: file.name.split('.').pop()?.toLowerCase() || 'jpg',
        mime_type: file.type,
        is_primary: images.length === 0 && idx === 0,
        sort_order: images.length + idx,
        uploading: true,
        progress: 0,
      };
    });

    // Add temp images immediately for UI feedback
    const updatedImages = [...images, ...tempImages];
    setImages(updatedImages);
    onChange(updatedImages);

    // Upload each file
    for (let i = 0; i < filesToUpload.length; i++) {
      const file = filesToUpload[i];
      const tempImage = tempImages[i];

      // Validate file size
      if (file.size > maxSizeMB * 1024 * 1024) {
        updateTempImage(tempImage.temp_id!, { error: `File too large (max ${maxSizeMB}MB)`, uploading: false });
        continue;
      }

      try {
        const compressedFile = await compressImageFile(file, 1920, 0.7);

        // Upload to static file server
        const result = await uploadFile(compressedFile, 'product-images');
        
        if (!result.success || !result.publicUrl) {
          throw new Error(result.error || 'Upload failed');
        }

        // If we have workItemId, save to database
        let savedImage: ProductImage | null = null;
        if (workItemId) {
          savedImage = await saveImageToDatabase({
            ...tempImage,
            file_name: compressedFile.name,
            file_path: result.publicUrl,
            file_size: compressedFile.size,
            file_type: compressedFile.name.split('.').pop()?.toLowerCase() || 'jpg',
            mime_type: compressedFile.type,
            uploading: false,
            progress: undefined,
            temp_id: undefined
          });
        }

        // Update the temp image with real data
        updateTempImage(tempImage.temp_id!, {
          id: savedImage?.id,
          file_name: compressedFile.name,
          file_path: result.publicUrl,
          file_size: compressedFile.size,
          file_type: compressedFile.name.split('.').pop()?.toLowerCase() || 'jpg',
          mime_type: compressedFile.type,
          uploading: false,
          progress: undefined,
          error: undefined
        });

      } catch (err: unknown) {
        console.error('Upload error:', err);
        updateTempImage(tempImage.temp_id!, {
          error: err instanceof Error ? err.message : 'Upload failed',
          uploading: false
        });
      }
    }
  }, [images, maxImages, disabled, workItemId, onChange, maxSizeMB, saveImageToDatabase, updateTempImage, trackBlobUrl]);

  const handleRemoveImage = async (index: number) => {
    const imageToRemove = images[index];

    // If it has an ID, delete from database
    if (imageToRemove.id && workItemId) {
      try {
        const token = getAuthToken();
        await fetch(`/api/v1/work-items/${workItemId}/product-images/${imageToRemove.id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } catch (err) {
        console.error('Error deleting image:', err);
      }
    }

    // Revoke the temp blob: preview URL if this image was never uploaded.
    revokeBlobUrl(imageToRemove.file_path);

    // Update local state
    const updated = images.filter((_, i) => i !== index);
    
    // If we removed the primary, make first image primary
    if (imageToRemove.is_primary && updated.length > 0) {
      updated[0].is_primary = true;
    }

    // Recalculate sort orders
    updated.forEach((img, i) => img.sort_order = i);
    
    setImages(updated);
    onChange(updated);
  };

  const handleSetPrimary = async (index: number) => {
    const updated = images.map((img, i) => ({
      ...img,
      is_primary: i === index
    }));

    setImages(updated);
    onChange(updated);

    // Update in database if applicable
    const image = updated[index];
    if (image.id && workItemId) {
      try {
        const token = getAuthToken();
        await fetch(`/api/v1/work-items/${workItemId}/product-images/${image.id}`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ is_primary: true })
        });
      } catch (err) {
        console.error('Error updating primary image:', err);
      }
    }
  };

  const handleUpdateCaption = async (index: number, caption: string) => {
    const updated = [...images];
    updated[index] = { ...updated[index], caption };
    
    setImages(updated);
    onChange(updated);

    // Update in database if applicable
    const image = updated[index];
    if (image.id && workItemId) {
      try {
        const token = getAuthToken();
        await fetch(`/api/v1/work-items/${workItemId}/product-images/${image.id}`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ caption })
        });
      } catch (err) {
        console.error('Error updating caption:', err);
      }
    }
  };

  // Simple drag & drop reordering
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const updated = [...images];
    const [removed] = updated.splice(draggedIndex, 1);
    updated.splice(index, 0, removed);
    
    // Update sort orders
    updated.forEach((img, i) => img.sort_order = i);
    
    setImages(updated);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    onChange(images);

    // TODO: Batch update sort orders in database
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFilesSelected(files);
    }
  }, [handleFilesSelected]);

  const handleDragOverDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {label}
          </h4>
          {helpText && (
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {helpText}
            </p>
          )}
        </div>
        <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          {images.length} / {maxImages}
        </span>
      </div>

      {/* Upload zone */}
      {!disabled && images.length < maxImages && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOverDrop}
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            isDark
              ? 'border-gray-600 hover:border-gray-500 bg-gray-800/50'
              : 'border-gray-300 hover:border-gray-400 bg-gray-50'
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptedTypes}
            multiple
            onChange={(e) => handleFilesSelected(e.target.files)}
            className="hidden"
          />
          <Camera className={`w-10 h-10 mx-auto mb-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
          <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Drag & drop images here, or{' '}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="text-blue-500 hover:text-blue-600 font-medium"
            >
              browse
            </button>
          </p>
          <p className={`text-xs mt-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            Max {maxSizeMB}MB per image • JPG, PNG, WebP, GIF
          </p>
        </div>
      )}

      {/* Loading existing */}
      {loadingExisting && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
          <span className={`ml-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Loading images...
          </span>
        </div>
      )}

      {/* Image grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {images.map((image, index) => (
            <div
              key={image.id || image.temp_id || index}
              draggable={!disabled && !image.uploading}
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
            >
              <ImageCard
                image={image}
                index={index}
                onRemove={() => handleRemoveImage(index)}
                onSetPrimary={() => handleSetPrimary(index)}
                onView={() => setLightboxIndex(index)}
                onUpdateCaption={(caption) => handleUpdateCaption(index, caption)}
                showPrimarySelector={showPrimarySelector}
                showCaptions={showCaptions}
                showAltText={showAltText}
                disabled={disabled || !!image.uploading}
                isDark={isDark}
                isDragging={draggedIndex === index}
              />
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loadingExisting && images.length === 0 && (
        <div className={`text-center py-8 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
          <ImageLucide className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
          <p className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            No product images yet
          </p>
          {!disabled && (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="mt-3 text-sm text-blue-500 hover:text-blue-600 font-medium"
            >
              Upload your first image
            </button>
          )}
        </div>
      )}

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <ImageLightbox
          images={images.filter(img => !img.error)}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={setLightboxIndex}
        />
      )}
    </div>
  );
}
